import { Box, Input, InputGroup, InputLeftElement, Icon, VStack, HStack, Text, useColorModeValue, Portal, Spinner, Divider, Badge } from "@chakra-ui/react";
import { IconSearch, IconX, IconBuilding, IconUsers, IconFileText, IconReceipt } from "@tabler/icons-react";
import { FunctionComponent, useRef, useState, useEffect, useMemo, useLayoutEffect, useCallback } from "react";
import { useGetClients } from "../../api/clients/clientQueries";
import { useGetEntitiesForUser } from "../../api/entities/entityQueries";
import { useGetTransactionsForUser } from "../../api/transactions/transactionQueries";
import { useGetReportsForUser } from "../../api/reports/reportQueries";
import { useGetFilesAndFolders } from "../../api/sharepoint/sharepointQueries";
import { FileResponse } from "../../api/sharepoint/sharepointApi";

interface SearchResultItem {
    id: string;
    title: string;
    subtitle?: string;
    category: "clients" | "entities" | "transactions" | "reports" | "file";
    route: string;
    metadata?: string;
}

interface DropdownSearchProps {
    placeholder?: string;
    autoFocus?: boolean;
    onClose?: () => void;
}

export const DropdownSearch: FunctionComponent<DropdownSearchProps> = ({
    placeholder = "Search clients, entities, transactions, reports...",
    autoFocus = false,
    onClose,
}) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [boundingClientRect, setBoundingClientRect] = useState<DOMRect | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const portalRef = useRef<HTMLDivElement>(null);

    // Fetch all data
    const { data: clients = [], isLoading: isLoadingClients } = useGetClients();
    const { data: entities = [], isLoading: isLoadingEntities } = useGetEntitiesForUser();
    const { data: transactions = [], isLoading: isLoadingTransactions } = useGetTransactionsForUser();
    const { data: reports = [], isLoading: isLoadingReports } = useGetReportsForUser();
    const { data: filesAndFolders = [], isLoading: isLoadingFilesAndFolders } = useGetFilesAndFolders();

    const isLoading = isLoadingClients || isLoadingEntities || isLoadingTransactions || isLoadingReports || isLoadingFilesAndFolders;

    // Color mode values
    const bgColor = useColorModeValue("white", "gray.800");
    const borderColor = useColorModeValue("gray.200", "gray.600");
    const hoverBg = useColorModeValue("gray.50", "gray.700");
    const selectedBg = useColorModeValue("blue.50", "blue.900");
    const shadowColor = useColorModeValue("lg", "dark-lg");
    const textSecondary = useColorModeValue("gray.600", "gray.400");

    // Filter and format search results
    const getSearchResults: SearchResultItem[] = useMemo(() => {
        if (!searchQuery.trim()) return [];

        const query = searchQuery.toLowerCase();
        const results: SearchResultItem[] = [];
        const files: FileResponse[] = filesAndFolders || [];

        // Search clients
        clients
            .filter(client => client.name?.toLowerCase().includes(query))
            .slice(0, 5)
            .forEach(client => {
                results.push({
                    id: client.id!,
                    title: client.name || "Unnamed Client",
                    subtitle: client.abbreviation || undefined,
                    category: "clients",
                    route: `/clients/${client.id}/dashboard`,
                    metadata: client.createdDate ? new Date(client.createdDate).toLocaleDateString() : undefined,
                });
            });

        // Search entities
        entities
            .filter(entity => entity.abbreviatedName?.toLowerCase().includes(query) || entity.fullLegalName?.toLowerCase().includes(query))
            .slice(0, 5)
            .forEach(entity => {
                results.push({
                    id: entity.id!,
                    title: entity.abbreviatedName || "Unnamed Entity",
                    subtitle: entity.fullLegalName || undefined,
                    category: "entities",
                    route: `/clients/${entity.clientId}/entities/${entity.id}`,
                    metadata: entity.yearEnded?.toString(),
                });
            });

        // Search transactions
        transactions
            .filter(transaction => transaction.name?.toLowerCase().includes(query))
            .slice(0, 5)
            .forEach(transaction => {
                results.push({
                    id: transaction.id!,
                    title: transaction.name || "Unnamed Transaction",
                    subtitle: transaction.type || undefined,
                    category: "transactions",
                    route: `/clients/${transaction.clientId}/transactions/${transaction.id}`,
                    metadata: transaction.year?.toString(),
                });
            });

        // Search reports
        reports
            .filter(report => report.generatedReport?.fileName?.toLowerCase().includes(query))
            .slice(0, 5)
            .forEach(report => {
                results.push({
                    id: report.id!,
                    title: report.generatedReport?.fileName || "Unnamed Report",
                    subtitle: report.generatedReport?.filePath || undefined,
                    category: "reports",
                    route: `/clients/${report.clientId}/reports/${report.id}`,
                    metadata: report.year?.toString(),
                });
            });

        // Search files and folders
        files
            .filter(file => file.name?.toLowerCase().includes(query))
            .slice(0, 5)
            .forEach(file => {
                results.push({
                    id: file.id!,
                    title: file.name || "Unnamed File",
                    subtitle: file.sharePointLink || undefined,
                    category: "file",
                    route: file.sharePointLink || `/sharepoint/${file.id}`,
                    metadata: file.uploadedOn ? new Date(file.uploadedOn).toLocaleDateString() : undefined,
                });
            });

        return results;
    }, [searchQuery, clients, entities, transactions, reports, filesAndFolders]);

    const searchResults = getSearchResults;

    // Group results by category
    const groupedResults = useMemo(
        () =>
            searchResults.reduce(
                (acc, item) => {
                    if (!acc[item.category]) {
                        acc[item.category] = [];
                    }
                    acc[item.category].push(item);
                    return acc;
                },
                {} as Record<string, SearchResultItem[]>,
            ),
        [searchResults],
    );

    // Category metadata
    const categoryInfo = {
        clients: { label: "Clients", icon: IconBuilding, color: "blue" },
        entities: { label: "Entities", icon: IconUsers, color: "green" },
        transactions: { label: "Transactions", icon: IconReceipt, color: "purple" },
        reports: { label: "Reports", icon: IconFileText, color: "orange" },
        file: { label: "Files", icon: IconFileText, color: "cyan" },
    };

    // Handle outside click to close dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (dropdownRef.current && !dropdownRef.current.contains(target) && portalRef.current && !portalRef.current.contains(target)) {
                setIsOpen(false);
                if (onClose) onClose();
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen, onClose]);

    // Reset selected index when results change
    useEffect(() => {
        setSelectedIndex(0);
    }, [searchQuery]);

    useLayoutEffect(() => {
        if (dropdownRef.current) {
            setBoundingClientRect(dropdownRef.current.getBoundingClientRect());
        }
    }, []);

    const handleSelectResult = useCallback(
        (result: SearchResultItem, source: "click" | "keyboard" = "keyboard") => {
            // You can now track whether the selection was via click or keyboard
            console.log(`Item selected via ${source}:`, result);

            window.open(result.route, "_blank");
            setSearchQuery("");
            setIsOpen(false);
            if (onClose) onClose();
        },
        [onClose],
    );

    // Handle keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isOpen) return;

            switch (e.key) {
                case "ArrowDown":
                    e.preventDefault();
                    setSelectedIndex(prev => (prev + 1) % searchResults.length);
                    break;
                case "ArrowUp":
                    e.preventDefault();
                    setSelectedIndex(prev => (prev - 1 + searchResults.length) % searchResults.length);
                    break;
                case "Enter":
                    e.preventDefault();
                    if (searchResults[selectedIndex]) {
                        handleSelectResult(searchResults[selectedIndex]);
                    }
                    break;
                case "Escape":
                    e.preventDefault();
                    setIsOpen(false);
                    if (onClose) onClose();
                    break;
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [handleSelectResult, isOpen, onClose, searchResults, selectedIndex]);

    const handleClearSearch = () => {
        setSearchQuery("");
        inputRef.current?.focus();
    };

    return (
        <Box position="relative" ref={dropdownRef} w="full" maxW="600px">
            <InputGroup size="lg">
                <InputLeftElement pointerEvents="none">
                    <Icon as={IconSearch} color="gray.400" />
                </InputLeftElement>
                <Input
                    ref={inputRef}
                    value={searchQuery}
                    onChange={e => {
                        setSearchQuery(e.target.value);
                        setIsOpen(true);
                    }}
                    onFocus={() => {
                        if (searchQuery) setIsOpen(true);
                    }}
                    placeholder={placeholder}
                    bg={bgColor}
                    borderColor={borderColor}
                    _hover={{ borderColor: "gray.400" }}
                    _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)" }}
                    autoFocus={autoFocus}
                    pr={searchQuery ? "2.5rem" : "1rem"}
                />
                {searchQuery && (
                    <Box position="absolute" right="12px" top="50%" transform="translateY(-50%)" cursor="pointer" onClick={handleClearSearch} zIndex={2}>
                        <Icon as={IconX} color="gray.400" _hover={{ color: "gray.600" }} />
                    </Box>
                )}
            </InputGroup>

            {/* Dropdown Results */}
            {isOpen && searchQuery && (
                <Portal>
                    <Box
                        ref={portalRef}
                        position="fixed"
                        top={`${(boundingClientRect?.bottom || 0) + 8}px`}
                        left={`${boundingClientRect?.left}px`}
                        w={`${boundingClientRect?.width}px`}
                        bg={bgColor}
                        borderRadius="md"
                        boxShadow={shadowColor}
                        border="1px solid"
                        borderColor={borderColor}
                        maxH="500px"
                        overflowY="auto"
                        zIndex={1500}
                    >
                        {isLoading ? (
                            <Box p={4} textAlign="center">
                                <Spinner size="sm" />
                                <Text mt={2} fontSize="sm" color={textSecondary}>
                                    Searching...
                                </Text>
                            </Box>
                        ) : searchResults.length === 0 ? (
                            <Box p={4} textAlign="center">
                                <Text fontSize="sm" color={textSecondary}>
                                    No results found for "{searchQuery}"
                                </Text>
                            </Box>
                        ) : (
                            <VStack align="stretch" spacing={0} py={2}>
                                {Object.entries(groupedResults).map(([category, items], categoryIndex) => {
                                    const info = categoryInfo[category as keyof typeof categoryInfo];
                                    const categoryStartIndex = searchResults.findIndex(r => r.category === category);

                                    return (
                                        <Box key={category}>
                                            {categoryIndex > 0 && <Divider />}

                                            {/* Category Header */}
                                            <HStack px={4} py={2} spacing={2}>
                                                <Icon as={info.icon} color={`${info.color}.500`} boxSize={4} />
                                                <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" color={textSecondary}>
                                                    {info.label}
                                                </Text>
                                                <Badge colorScheme={info.color} fontSize="xs">
                                                    {items.length}
                                                </Badge>
                                            </HStack>

                                            {/* Category Items */}
                                            {items.map((item, itemIndex) => {
                                                const absoluteIndex = categoryStartIndex + itemIndex;
                                                const isSelected = absoluteIndex === selectedIndex;

                                                return (
                                                    <Box
                                                        key={item.id}
                                                        px={4}
                                                        py={3}
                                                        cursor="pointer"
                                                        bg={isSelected ? selectedBg : "transparent"}
                                                        _hover={{ bg: hoverBg }}
                                                        onClick={() => handleSelectResult(item, "click")}
                                                        onMouseEnter={() => setSelectedIndex(absoluteIndex)}
                                                    >
                                                        <HStack justify="space-between" align="start">
                                                            <VStack align="start" spacing={0} flex={1}>
                                                                <Text fontWeight="medium" fontSize="sm">
                                                                    {item.title}
                                                                </Text>
                                                                {item.subtitle && (
                                                                    <Text fontSize="xs" color={textSecondary}>
                                                                        {item.subtitle}
                                                                    </Text>
                                                                )}
                                                            </VStack>
                                                            {item.metadata && (
                                                                <Badge fontSize="xs" colorScheme="gray">
                                                                    {item.metadata}
                                                                </Badge>
                                                            )}
                                                        </HStack>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    );
                                })}
                            </VStack>
                        )}
                    </Box>
                </Portal>
            )}
        </Box>
    );
};
